import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Space, Tag } from "antd";
import { SearchOutlined, UserOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import Highlighter from "react-highlight-words";

import InvoicesService from "../../services/http/services/InvoicesService";
import useAuth from "../../hooks/useAuth";
import { EmitError } from "../../utils/EmitError";
import { IDuplicatedInvoices } from "../../interfaces";
import { convertMoedaBRDoJSNovo } from "../../utils/Conversores";
import { ThemeContext } from "../../contexts/ThemeContext";
import TableActionButton from "../../components/TableActionButton";
import { useTableParams } from "../../hooks/useTableParams";
import { getSortOrder } from "../../hooks/SortOrder";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";
import { findChunksText, HIGHLIGHT_STYLE } from "../../utils/Highlight";

export interface ITableData extends IDuplicatedInvoices {
    rowKey: string;
}

export type TypeSortDuplicated = 'appUser' | 'value' | 'total';

export interface ICityInvoicesDuplicatedFilters {
    search: string | null;
    type: string | null;
}

export default function useCityInvoices() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const navigate = useNavigate();
    const { activeCity } = useAuth();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [rawData, setRawData] = useState<ITableData[]>([]);
    const [showModalInvoice, setShowModalInvoice] = useState<boolean>(false);
    const [invoiceSelectedKey, setInvoiceSelectedKey] = useState<string>();

    const searchInputRef = useRef<any>(null);

    const {
        tableParams,
        handleTableChange,
        handleCustomChange,
        handlePaginationChange,
        handleFilterClear,
        handleClearAllFilters,
    } = useTableParams<ICityInvoicesDuplicatedFilters, TypeSortDuplicated>({
        storageKey: "@NotaMunicipio:invoicesDuplicated",
        defaultFilters: {
            search: null,
            type: null,
        },
        defaultSort: {
            field: "value",
            order: "desc",
        },
        defaultPageSize: 50,
    });

    const [searchValue, setSearchValue] = useState(tableParams.filters.search || "");

    useEffect(() => {
        setSearchValue(tableParams.filters.search || "");
    }, [tableParams.filters.search]);

    const handleShowModalInvoice = useCallback(() => {
        setShowModalInvoice((prevState) => !prevState);
    }, []);

    const setTableDataFromRequest = useCallback((data: IDuplicatedInvoices[]) => {
        const newData: ITableData[] = (data || []).map((group) => ({
            ...group,
            rowKey: `${group.appUser.id}-${group.value}`,
        }));

        setRawData(newData);
    }, []);

    const reloadInvoices = useCallback(async () => {
        const controller = new AbortController();
        if (!activeCity?.id) {
            return;
        }

        try {
            setIsLoading(true);
            const response = await InvoicesService.listInvoicesDuplicated({
                cityId: activeCity.id,
                signal: controller.signal,
            });
            setTableDataFromRequest(response);
            setIsLoading(false);
        } catch (err) {
            if (!(err instanceof AxiosError && err.name === "CanceledError")) {
                EmitError(err, true, "cityInvoicesDuplicated");
                setIsLoading(false);
            }
        }
    }, [activeCity?.id, setTableDataFromRequest]);

    useEffect(() => {
        const controller = new AbortController();

        async function fetchInvoices() {
            try {
                setIsLoading(true);
                const response = await InvoicesService.listInvoicesDuplicated({
                    cityId: activeCity?.id!,
                    signal: controller.signal,
                });

                setTableDataFromRequest(response);
                setIsLoading(false);
            } catch (err) {
                if (!(err instanceof AxiosError && err.name === "CanceledError")) {
                    EmitError(err, true, "cityInvoicesDuplicated");
                    setIsLoading(false);
                }
            }
        }

        if (activeCity?.id) {
            fetchInvoices();
        }

        return () => controller.abort();
    }, [activeCity, setTableDataFromRequest]);

    const filteredAndSortedData = useMemo(() => {
        let result = [...rawData];

        if (tableParams.filters.search) {
            const query = tableParams.filters.search.toLowerCase().trim();
            result = result.filter((item) => {
                const nameMatch = item.appUser?.name?.toLowerCase().includes(query);
                const idMatch = String(item.appUser?.id).includes(query);
                return nameMatch || idMatch;
            });
        }

        if (tableParams.filters.type) {
            result = result.filter((item) =>
                item.typeList?.includes(tableParams.filters.type!)
            );
        }

        const { field, order } = tableParams.sorter;
        if (field) {
            result.sort((a, b) => {
                let valA: any = a[field as keyof ITableData];
                let valB: any = b[field as keyof ITableData];

                if (field === "appUser") {
                    valA = a.appUser?.name?.toLowerCase() || "";
                    valB = b.appUser?.name?.toLowerCase() || "";
                }

                if (typeof valA === "string") {
                    return order === "asc"
                        ? valA.localeCompare(valB)
                        : valB.localeCompare(valA);
                }

                if (typeof valA === "number") {
                    return order === "asc" ? valA - valB : valB - valA;
                }

                return 0;
            });
        }

        return result;
    }, [rawData, tableParams.filters, tableParams.sorter]);

    const paginatedTableData = useMemo(() => {
        const { current, pageSize } = tableParams.pagination;
        const start = (current - 1) * pageSize;
        return filteredAndSortedData.slice(start, start + pageSize);
    }, [filteredAndSortedData, tableParams.pagination]);

    const activeFilterItems: Array<IActiveFilterItem | null> = [
        tableParams.filters.search
            ? {
                key: "search",
                label: "Usuário",
                value: tableParams.filters.search,
                onClear: () => {
                    setSearchValue("");
                    handleFilterClear("search");
                },
            }
            : null,
        tableParams.filters.type
            ? {
                key: "type",
                label: "Tipo",
                value: tableParams.filters.type,
                onClear: () => handleFilterClear("type"),
            }
            : null,
    ];

    const columns: ColumnsType<ITableData> = [
        {
            title: "Usuário",
            dataIndex: "appUser",
            key: "appUser",
            sorter: true,
            sortOrder: getSortOrder("appUser", tableParams.sorter),
            showSorterTooltip: false,
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 260, display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#ccc" : "#475569", marginBottom: 2 }}>
                        Buscar por Usuário
                    </div>
                    <Input
                        ref={searchInputRef}
                        placeholder="Nome ou ID do usuário..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onPressEnter={() => {
                            confirm();
                            handleCustomChange({
                                newPage: 1,
                                newFilters: { search: searchValue.trim() || null },
                            });
                        }}
                        allowClear
                    />
                    <Space style={{ justifyContent: "space-between", marginTop: 4 }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => {
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: { search: searchValue.trim() || null },
                                });
                            }}
                            style={{ minWidth: 100 }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setSearchValue("");
                                if (clearFilters) clearFilters();
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: { search: null },
                                });
                            }}
                            style={{ minWidth: 80 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: tableParams.filters.search ? "#1677ff" : undefined }} />
            ),
            onFilterDropdownOpenChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => searchInputRef.current?.focus(), 100);
                }
            },
            render: (appUser: { id: number; name: string }) => (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 600, color: isDark ? "#f0f0f0" : "#1e293b", fontSize: 13 }}>
                        {tableParams.filters.search ? (
                            <Highlighter
                                highlightStyle={HIGHLIGHT_STYLE}
                                searchWords={[tableParams.filters.search]}
                                findChunks={findChunksText}
                                autoEscape
                                textToHighlight={appUser?.name || "--"}
                            />
                        ) : (
                            appUser?.name || "--"
                        )}
                    </span>
                    <span style={{ fontSize: 11, color: isDark ? "#8c8c8c" : "#94a3b8" }}>
                        #{appUser?.id}
                    </span>
                </div>
            ),
            width: 280,
        },
        {
            title: "Valor",
            dataIndex: "value",
            key: "value",
            align: "right" as const,
            sorter: true,
            sortOrder: getSortOrder("value", tableParams.sorter),
            showSorterTooltip: false,
            render: (value: number) => (
                <span style={{ fontWeight: 700, color: isDark ? "#52c41a" : "#237804", fontSize: 13.5, whiteSpace: "nowrap" }}>
                    R$ {convertMoedaBRDoJSNovo(value)}
                </span>
            ),
            width: 150,
        },
        {
            title: "Tipos Envolvidos",
            dataIndex: "typeList",
            key: "typeList",
            align: "center" as const,
            filters: [
                { text: "NFS", value: "NFS" },
                { text: "NFC", value: "NFC" },
                { text: "NFE", value: "NFE" },
            ],
            filteredValue: tableParams.filters?.type ? [tableParams.filters.type] : null,
            filterMultiple: false,
            render: (typeList: string[]) => (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, flexWrap: "wrap" }}>
                    {typeList?.map((type) => {
                        let color = "blue";
                        if (type === "NFC") color = "purple";
                        if (type === "NFE") color = "cyan";
                        return (
                            <Tag
                                key={type}
                                color={color}
                                style={{ borderRadius: 10, fontWeight: 600, fontSize: 11, margin: 0 }}
                            >
                                {type}
                            </Tag>
                        );
                    })}
                </div>
            ),
            width: 200,
        },
        {
            title: "Notas no Grupo",
            dataIndex: "total",
            key: "total",
            align: "center" as const,
            sorter: true,
            sortOrder: getSortOrder("total", tableParams.sorter),
            showSorterTooltip: false,
            render: (total: number) => (
                <Tag
                    color="#1677ff"
                    style={{ borderRadius: 10, fontWeight: 600, fontSize: 11, margin: 0 }}
                >
                    {total} {total === 1 ? "nota" : "notas"}
                </Tag>
            ),
            width: 140,
        },
        {
            title: "Ações",
            key: "actions",
            align: "center" as const,
            width: 100,
            fixed: "right" as const,
            render: (_: any, record: ITableData) => (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                    <TableActionButton
                        action="view"
                        tooltip="Comparar e validar notas"
                        onClick={(e) => {
                            e?.stopPropagation?.();
                            setInvoiceSelectedKey(record.rowKey);
                            handleShowModalInvoice();
                        }}
                    />
                    <TableActionButton
                        action="custom"
                        tooltip="Ver cadastro do usuário"
                        icon={<UserOutlined style={{ fontSize: 14, color: "#52c41a" }} />}
                        color="#52c41a"
                        bgColor={isDark ? "rgba(82, 196, 26, 0.15)" : "#f6ffed"}
                        onClick={(e) => {
                            e?.stopPropagation?.();
                            navigate(`/usuarios/aplicativo/visualizar/${record.appUser.id}`);
                        }}
                    />
                </div>
            ),
        },
    ];

    const invoiceSelected = useMemo(() => {
        return rawData.find((item) => item.rowKey === invoiceSelectedKey);
    }, [rawData, invoiceSelectedKey]);

    return {
        columns,
        isLoading,
        tableData: paginatedTableData,
        totalItems: filteredAndSortedData.length,
        rawTotalCount: rawData.length,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        tableParams,
        activeFilterItems,
        showModalInvoice,
        handleShowModalInvoice,
        invoiceSelected,
        setInvoiceSelectedKey,
        reloadInvoices,
    };
}